import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, Grow } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  ThemeScope, Button, Input, Label, Card, CardHeader, CardTitle, CardDescription,
  CardContent, CardFooter, Alert, Badge, Separator,
} from "./ui/shadcn";

/* ──────────────────────────────────────────────────────────────
   LoginDesign07 — "Bold editorial"
   Warm sand paper, huge condensed headline, sharp-cornered form
   panel, orange accent and strong ruled grid lines.
   Built with shadcn-style primitives (./ui/shadcn) + framer-motion.
   All login logic is identical to Login.js.
   ────────────────────────────────────────────────────────────── */

const INK = "#17120c";
const SAND = "#efe5d2";
const SAND_DEEP = "#e4d6bb";
const PAPER = "#fffcf5";
const ACCENT = "#f04e0f";
const MUTED = "#4a4036";
const DANGER = "#b3261e";

const FONT_BODY = "'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif";
const FONT_DISPLAY = "'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace";

// shadcn tokens for this concept: sharp corners, ink borders, orange ring
const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": PAPER,
  "--card-foreground": INK,
  "--primary": INK,
  "--primary-foreground": PAPER,
  "--secondary": SAND,
  "--secondary-foreground": INK,
  "--muted": SAND,
  "--muted-foreground": MUTED,
  "--accent": SAND,
  "--destructive": DANGER,
  "--destructive-foreground": "#ffffff",
  "--border": INK,
  "--input": INK,
  "--ring": ACCENT,
  "--radius": "0px",
};

const MButton = motion.create(Button);
const MCard = motion.create(Card);

const EASE = [0.22, 1, 0.36, 1];

const LOGIN_CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap&family=IBM+Plex+Mono:wght@400;500;600');
.ld7-root, .ld7-root * { font-family: ${FONT_BODY} !important; }
.ld7-root .ld7-mono, .ld7-root .ld7-mono * { font-family: ${FONT_MONO} !important; }
.ld7-root .ld7-display, .ld7-root .ld7-display * { font-family: ${FONT_DISPLAY} !important; font-stretch: 78%; }
.ld7-root ::selection { background: ${ACCENT}; color: ${INK}; }
.ld7-root p, .ld7-root h1, .ld7-root h2, .ld7-root ul { margin: 0; }

/* shell + frame */
.ld7-shell { min-height: 100vh; min-height: 100dvh; width: 100%; max-width: 100vw; overflow-x: hidden; display: flex; flex-direction: column; background: ${SAND_DEEP}; color: ${INK}; padding: 8px; }
.ld7-frame { flex: 1 0 auto; min-width: 0; display: flex; flex-direction: column; border: 2px solid ${INK}; background-color: ${SAND}; background-image: linear-gradient(rgba(23,18,12,0.075) 1px, transparent 1px), linear-gradient(90deg, rgba(23,18,12,0.075) 1px, transparent 1px); background-size: 72px 72px; }
@media (min-width: 600px) { .ld7-shell { padding: 12px; } }
@media (min-width: 1200px) { .ld7-shell { padding: 20px; } }

/* masthead */
.ld7-masthead { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; border-bottom: 2px solid ${INK}; background: ${SAND}; }
.ld7-mh-logo { display: flex; align-items: center; padding: 10px 16px; border-right: 2px solid ${INK}; }
.ld7-mh-logo img { height: 28px; display: block; filter: brightness(0); }
.ld7-mh-tag { display: flex; align-items: center; padding: 0 12px; min-width: 0; }
.ld7-mh-tag span { font-size: 0.62rem; font-weight: 500; letter-spacing: 0.16em; text-transform: uppercase; color: ${MUTED}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ld7-mh-edition { display: none; align-items: center; gap: 10px; padding: 0 24px; border-left: 2px solid ${INK}; }
.ld7-dot { width: 9px; height: 9px; background: ${ACCENT}; display: block; }
.ld7-root .ui-badge.ld7-ed { border-radius: 0; border: 1.5px solid ${INK}; background: transparent; color: ${INK}; padding: 3px 9px; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; }
@media (min-width: 600px) { .ld7-mh-edition { display: flex; } .ld7-mh-tag span { font-size: 0.7rem; } .ld7-mh-tag { padding: 0 24px; } }
@media (min-width: 900px) { .ld7-mh-logo { padding: 12px 24px; } .ld7-mh-logo img { height: 36px; } }

/* main grid */
.ld7-main { flex: 1; min-width: 0; display: grid; grid-template-columns: minmax(0, 1fr); grid-template-areas: "panel" "hero" "features"; }
@media (min-width: 1200px) { .ld7-main { grid-template-columns: minmax(0, 1.28fr) minmax(0, 1fr); grid-template-rows: 1fr auto; grid-template-areas: "hero panel" "features panel"; } }

/* hero */
.ld7-hero { grid-area: hero; min-width: 0; display: flex; flex-direction: column; justify-content: center; padding: 20px 16px 22px; border-bottom: 2px solid ${INK}; }
.ld7-eyebrow { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
@media (max-width: 599px) { .ld7-lede { display: none; } }
.ld7-eyebrow-sq { width: 11px; height: 11px; background: ${ACCENT}; flex-shrink: 0; }
.ld7-eyebrow-txt { font-size: 0.66rem; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; }
.ld7-eyebrow-rule { flex: 1; height: 2px; min-width: 16px; background: ${INK}; transform-origin: left center; }
.ld7-h1 { font-weight: 900; text-transform: uppercase; color: ${INK}; letter-spacing: -0.025em; line-height: 0.95; font-size: clamp(2.4rem, 13.5vw, 3.6rem); }
.ld7-line { display: block; overflow: hidden; padding-bottom: 0.08em; margin-bottom: -0.08em; }
.ld7-line > span { display: block; }
.ld7-block { display: inline-block; background: ${INK}; color: ${SAND}; padding: 0.03em 0.1em 0; margin-top: 0.03em; }
.ld7-block i { font-style: normal; color: ${ACCENT}; }
.ld7-lede { margin-top: 24px; max-width: 460px; padding-left: 16px; border-left: 4px solid ${ACCENT}; color: ${MUTED}; font-size: 0.95rem; line-height: 1.7; font-weight: 500; }
@media (min-width: 600px) { .ld7-hero { padding: 36px 32px; } .ld7-eyebrow { margin-bottom: 20px; } .ld7-h1 { font-size: clamp(3.4rem, 12vw, 5.8rem); } .ld7-eyebrow-txt { font-size: 0.72rem; } .ld7-lede { font-size: 1rem; } }
@media (min-width: 1200px) {
  .ld7-hero { padding: 40px 56px; border-bottom: 0; border-right: 2px solid ${INK}; }
  .ld7-eyebrow { margin-bottom: 24px; }
  .ld7-h1 { font-size: clamp(4rem, min(8.4vw, 15.5vh), 9.75rem); }
  .ld7-lede { margin-top: 32px; }
}

/* panel column */
.ld7-panelcell { grid-area: panel; min-width: 0; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; background: rgba(228,214,187,0.55); padding: 22px 22px 30px 14px; border-bottom: 2px solid ${INK}; }
@media (min-width: 600px) { .ld7-panelcell { padding: 36px 44px 44px 32px; } }
@media (min-width: 1600px) { .ld7-panelwrap { max-width: 520px; } }
@media (min-width: 1200px) { .ld7-panelcell { padding: 48px 60px 56px 48px; border-bottom: 0; } }
@media (min-width: 1200px) and (max-height: 760px) {
  .ld7-panelcell { padding: 28px 48px 36px 40px; }
  .ld7-root .ui-card-header.ld7-ch { padding-top: 22px; }
  .ld7-root .ui-card-title.ld7-ct { font-size: 2.3rem; }
  .ld7-root .ui-card-title.ld7-ct.is-long { font-size: 2rem; }
  .ld7-root .ui-card-content.ld7-cc { padding-top: 18px; }
  .ld7-root .ui-card-footer.ld7-cf { padding-top: 18px; padding-bottom: 20px; }
}
.ld7-bignum { display: none; position: absolute; right: -1%; bottom: -7%; z-index: 0; font-weight: 900; line-height: 0.8; font-size: clamp(14rem, 24vw, 24rem); color: transparent; -webkit-text-stroke: 2px rgba(23,18,12,0.22); user-select: none; pointer-events: none; }
@media (min-width: 1200px) { .ld7-bignum { display: block; } }
.ld7-panelwrap { position: relative; z-index: 1; width: 100%; max-width: 460px; }
.ld7-shadowblock { position: absolute; inset: 0; background: ${ACCENT}; z-index: 0; }
.ld7-root .ui-card.ld7-panel { position: relative; z-index: 1; border: 2px solid ${INK}; border-radius: 0; background: ${PAPER}; box-shadow: none; }
.ld7-tab { display: flex; align-items: stretch; justify-content: space-between; background: ${INK}; color: ${PAPER}; }
.ld7-tab span { padding: 10px 20px; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; }
.ld7-tab i { width: 44px; background: ${ACCENT}; display: block; }
.ld7-root .ui-card-header.ld7-ch { padding: 22px 20px 0; gap: 8px; }
.ld7-root .ui-card-title.ld7-ct { font-weight: 900; text-transform: uppercase; letter-spacing: -0.02em; line-height: 0.95; font-size: clamp(2.1rem, 9.5vw, 3rem); color: ${INK}; overflow-wrap: anywhere; }
.ld7-root .ui-card-title.ld7-ct.is-long { font-size: clamp(1.85rem, 8.2vw, 2.6rem); }
.ld7-ct i { font-style: normal; color: ${ACCENT}; }
.ld7-root .ui-card-description.ld7-cd { color: ${MUTED}; font-size: 0.92rem; font-weight: 500; line-height: 1.5; }
.ld7-root .ui-card-content.ld7-cc { padding: 22px 20px 0; }
.ld7-root .ui-card-footer.ld7-cf { flex-direction: column; align-items: stretch; gap: 0; padding: 22px 20px 20px; }
@media (min-width: 600px) {
  .ld7-root .ui-card-header.ld7-ch { padding: 32px 32px 0; gap: 10px; }
  .ld7-root .ui-card-content.ld7-cc { padding: 28px 32px 0; }
  .ld7-root .ui-card-footer.ld7-cf { padding: 26px 32px 26px; }
}
/* short viewports (landscape phones): tighten vertical rhythm, page still scrolls */
@media (max-height: 520px) {
  .ld7-panelcell { padding-top: 16px; padding-bottom: 24px; }
  .ld7-tab span { padding-top: 7px; padding-bottom: 7px; }
  .ld7-root .ui-card-header.ld7-ch { padding-top: 16px; gap: 6px; }
  .ld7-root .ui-card-title.ld7-ct { font-size: 2rem; }
  .ld7-root .ui-card-title.ld7-ct.is-long { font-size: 1.8rem; }
  .ld7-root .ui-card-content.ld7-cc { padding-top: 14px; }
  .ld7-root .ui-card-footer.ld7-cf { padding-top: 14px; padding-bottom: 16px; }
  .ld7-mh-logo { padding-top: 6px; padding-bottom: 6px; }
  .ld7-hero { padding-top: 20px; padding-bottom: 22px; }
}
.ld7-root .ui-sep { height: 2px; background: ${INK}; }
.ld7-signup { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; column-gap: 16px; row-gap: 14px; padding-top: 18px; font-size: 0.875rem; font-weight: 500; color: ${MUTED}; }

/* form fields */
.ld7-field { margin-bottom: 4px; }
.ld7-root .ui-label.ld7-lab { margin-bottom: 8px; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; line-height: 1.3; color: ${INK}; }
.ld7-inputwrap { position: relative; }
.ld7-root .ui-input { height: 54px; border-radius: 0; border: 1.5px solid ${INK}; background: #fff; color: ${INK}; font-size: 1rem; font-weight: 500; padding: 0 14px; }
.ld7-root .ui-input:hover { border-color: ${INK}; }
.ld7-root .ui-input:focus-visible { box-shadow: 4px 4px 0 ${ACCENT}; border-color: ${INK}; }
.ld7-root .ui-input:-webkit-autofill, .ld7-root .ui-input:-webkit-autofill:hover, .ld7-root .ui-input:-webkit-autofill:focus { -webkit-text-fill-color: ${INK}; caret-color: ${INK}; border-color: ${INK}; -webkit-box-shadow: 0 0 0 1000px #fff inset, 4px 4px 0 ${ACCENT}; box-shadow: 0 0 0 1000px #fff inset, 4px 4px 0 ${ACCENT}; }
.ld7-root .ui-input:-webkit-autofill:not(:focus) { -webkit-box-shadow: 0 0 0 1000px #fff inset; box-shadow: 0 0 0 1000px #fff inset; }
.ld7-field .ui-input { padding-right: 44px; }
.ld7-field.ld7-pw .ui-input { padding-right: 90px; }
.ld7-root .ui-btn.ld7-eye { position: absolute; top: 0; right: 0; height: 54px; width: 48px; padding: 0; border-radius: 0; background: transparent; color: ${INK}; }
.ld7-root .ui-btn.ld7-eye:hover { background: ${SAND}; }
.ld7-capsicon { position: absolute; top: 50%; margin-top: -10px; right: 14px; color: #9a4a00; font-size: 20px !important; pointer-events: none; }
.ld7-pw .ld7-capsicon { right: 54px; }
.ld7-help { min-height: 24px; padding-top: 6px; font-size: 12px; line-height: 1.4; }
.ld7-caps { display: inline-flex; align-items: center; gap: 4px; color: #9a4a00; font-weight: 600; }
.ld7-forgot { display: flex; justify-content: flex-end; margin: 6px 0 22px; }
.ld7-root .ld7-tap { position: relative; }
.ld7-root .ld7-tap::before { content: ""; position: absolute; inset: -14px -10px; }
.ld7-root .ld7-link { color: ${INK}; font-weight: 700; text-decoration: underline; text-decoration-color: ${ACCENT}; text-decoration-thickness: 2px; text-underline-offset: 4px; transition: background-color 0.2s ease, text-decoration-color 0.2s ease; }
.ld7-root .ld7-link:hover { background-color: ${ACCENT}; text-decoration-color: ${INK}; }
.ld7-root a:focus-visible { outline: 3px solid ${ACCENT}; outline-offset: 2px; }

/* buttons */
.ld7-root .ui-btn:focus-visible { outline: 3px solid ${ACCENT}; outline-offset: 3px; box-shadow: none; }
.ld7-root .ui-btn.ld7-cta { width: 100%; height: auto; min-height: 56px; justify-content: space-between; padding: 14px 20px; border-radius: 0; border: 2px solid ${INK}; background: ${INK}; color: ${PAPER}; font-size: 0.92rem; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; line-height: 1.2; }
.ld7-root .ui-btn.ld7-cta:hover { background: ${ACCENT}; color: ${INK}; opacity: 1; }
.ld7-root .ui-btn.ld7-cta:disabled { background: #6b6258; border-color: #6b6258; color: #fff; opacity: 1; }
.ld7-cta-arrow { display: inline-flex; }
.ld7-root .ui-btn.ld7-bio { width: 100%; height: auto; margin-top: 12px; padding: 13px 16px; border-radius: 0; border: 2px solid ${INK}; background: transparent; color: ${INK}; font-size: 0.8rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; }
.ld7-root .ui-btn.ld7-bio:hover { background: ${SAND}; }

/* error alert */
.ld7-root .ui-alert.ld7-alert { display: flex; align-items: flex-start; gap: 8px; background: #fdecea; color: #8c1d18; border: 1px solid ${DANGER}; border-left-width: 5px; border-radius: 0; padding: 12px 14px; font-size: 0.85rem; font-weight: 500; line-height: 1.5; }
.ld7-alert-wrap { overflow: hidden; }
.ld7-alert-inner { padding-bottom: 20px; }

/* company cards */
.ld7-colist { display: flex; flex-direction: column; gap: 10px; max-height: min(340px, 42vh); overflow-y: auto; padding: 4px 10px 10px 4px; margin: -4px -10px 14px -4px; }
.ld7-root .ui-card.ld7-co { border: 1.5px solid ${INK}; border-radius: 0; background: #fff; box-shadow: none; transition: background-color 0.2s ease, box-shadow 0.2s ease; }
.ld7-root .ui-card.ld7-co:hover { box-shadow: 4px 4px 0 ${INK}; }
.ld7-root .ui-card.ld7-co.is-selected { background: ${ACCENT}; box-shadow: 4px 4px 0 ${INK}; }
.ld7-root .ui-card.ld7-co:focus-within { outline: 3px solid ${ACCENT}; outline-offset: 3px; }
.ld7-co-label { display: flex; align-items: center; gap: 14px; padding: 14px 16px; cursor: pointer; min-width: 0; }
.ld7-co-num { flex-shrink: 0; background: ${INK}; color: ${PAPER}; padding: 2px 7px; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.08em; }
.ld7-co-name { flex: 1; min-width: 0; font-weight: 700; font-size: 0.95rem; line-height: 1.3; overflow-wrap: anywhere; }
.ld7-co-check { flex-shrink: 0; width: 24px; height: 24px; border: 1.5px solid ${INK}; display: flex; align-items: center; justify-content: center; }
.ld7-co.is-selected .ld7-co-check { background: ${INK}; color: ${PAPER}; }
.ld7-sr { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); border: 0; white-space: nowrap; }

/* features */
.ld7-features { grid-area: features; min-width: 0; padding: 0; list-style: none; display: grid; grid-template-columns: minmax(0, 1fr); gap: 2px; background: ${INK}; }
.ld7-features li { display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: ${SAND}; font-size: 0.9rem; font-weight: 600; line-height: 1.4; }
.ld7-root .ui-badge.ld7-fbadge { flex-shrink: 0; border-radius: 0; background: ${ACCENT}; color: ${INK}; padding: 2px 7px; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.08em; }
@media (min-width: 600px) { .ld7-features { grid-template-columns: repeat(2, minmax(0, 1fr)); } .ld7-features li { padding: 14px 24px; } }
@media (min-width: 1200px) { .ld7-features { border-top: 2px solid ${INK}; border-right: 2px solid ${INK}; } .ld7-features li { padding: 16px 28px; } }

/* ticker + footer */
@keyframes ld7-marquee { from { transform: translate3d(0,0,0); } to { transform: translate3d(-50%,0,0); } }
.ld7-track.ld-motion { animation: ld7-marquee 80s linear infinite !important; will-change: transform; backface-visibility: hidden; }
.ld7-ticker:hover .ld7-track.ld-motion { animation-play-state: paused; }
.ld7-ticker { border-top: 2px solid ${INK}; background: ${ACCENT}; color: ${INK}; overflow: hidden; white-space: nowrap; }
.ld7-track { display: inline-flex; will-change: transform; }
.ld7-tgroup { display: inline-flex; align-items: center; flex-shrink: 0; }
.ld7-titem { display: inline-flex; align-items: center; }
.ld7-titem .t { padding: 7px 0; font-size: 0.95rem; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; }
.ld7-diamond { width: 8px; height: 8px; background: ${INK}; transform: rotate(45deg); margin: 0 24px; flex-shrink: 0; }
.ld7-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; column-gap: 24px; row-gap: 4px; padding: 10px 16px; border-top: 2px solid ${INK}; background: ${SAND}; color: ${MUTED}; font-size: 0.7rem; font-weight: 500; }
.ld7-footer-r { display: none; letter-spacing: 0.16em; text-transform: uppercase; }
@media (min-width: 600px) { .ld7-footer { padding: 10px 24px; } .ld7-footer-r { display: block; } }

/* session dialog */
.ld7-dlg { background: ${PAPER}; color: ${INK}; }
.ld7-dlg-banner { background: ${INK}; color: ${PAPER}; padding: 24px 24px 20px; }
.ld7-dlg-head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
.ld7-dlg-icon { width: 42px; height: 42px; background: ${ACCENT}; color: ${INK}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ld7-dlg-title { font-weight: 900; font-size: 1.45rem; text-transform: uppercase; letter-spacing: -0.01em; line-height: 1.05; color: ${PAPER}; }
.ld7-dlg-sub { font-size: 0.7rem; letter-spacing: 0.06em; color: rgba(255,252,245,0.75); margin-top: 5px; }
.ld7-dlg-note { border-left: 4px solid ${ACCENT}; padding: 2px 0 2px 14px; font-size: 0.85rem; color: rgba(255,252,245,0.88); line-height: 1.55; font-weight: 500; }
.ld7-dlg-body { padding: 18px 20px 8px; }
.ld7-dlg-label { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.ld7-dlg-label span { font-size: 0.7rem; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; white-space: nowrap; }
.ld7-sess { display: flex; align-items: center; gap: 12px; padding: 12px; margin-bottom: 10px; border: 1.5px solid ${INK}; background: #fff; transition: box-shadow 0.18s ease; }
.ld7-sess:hover { box-shadow: 4px 4px 0 ${ACCENT}; }
.ld7-sess-icon { width: 38px; height: 38px; background: ${SAND}; border: 1.5px solid ${INK}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ld7-sess-info { flex: 1; min-width: 0; }
.ld7-sess-name { font-weight: 700; font-size: 0.88rem; color: ${INK}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ld7-sess-meta { display: flex; align-items: center; gap: 4px; margin-top: 3px; min-width: 0; font-size: 0.7rem; color: ${MUTED}; }
.ld7-sess-meta span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ld7-root .ui-btn.ld7-signout { flex-shrink: 0; min-width: 80px; min-height: 44px; height: auto; padding: 8px 12px; border-radius: 0; border: 1.5px solid ${DANGER}; background: ${DANGER}; color: #fff; font-size: 0.7rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.ld7-root .ui-btn.ld7-signout:hover { background: ${INK}; border-color: ${INK}; opacity: 1; }
.ld7-root .ui-btn.ld7-signout:disabled { background: #d9a29e; border-color: #d9a29e; opacity: 1; }
.ld7-dlg-foot { padding: 4px 20px 20px; }
.ld7-root .ui-btn.ld7-cancel { width: 100%; min-height: 46px; height: auto; padding: 11px 16px; border-radius: 0; border: 2px solid ${INK}; background: transparent; color: ${INK}; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; }
.ld7-root .ui-btn.ld7-cancel:hover { background: ${SAND}; }

@media (prefers-reduced-motion: reduce) { .ld7-root, .ld7-root *, .ld7-root *::before, .ld7-root *::after { transition: none !important; } }
`;

function LoginDesign07({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: INK }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: INK }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: INK }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: INK }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  /* ───────────── visual layer: framer-motion + shadcn-style primitives ───────────── */

  const rm = !!useReducedMotion();
  const dur = (d) => (rm ? 0 : d);

  const stagger = (delay = 0, gap = 0.08) => ({
    hidden: {},
    show: { transition: { delayChildren: dur(delay), staggerChildren: dur(gap) } },
  });
  const rise = {
    hidden: { opacity: 1, y: rm ? 0 : 18 },
    show: { opacity: 1, y: 0, transition: { duration: dur(0.65), ease: EASE } },
  };
  // Form controls never animate opacity (only a small offset), so inputs stay visible
  // even if an animation is interrupted or the tab is backgrounded mid-entrance.
  const riseSafe = {
    hidden: { y: rm ? 0 : 14 },
    show: { y: 0, transition: { duration: dur(0.55), ease: EASE } },
  };
  const lineRise = {
    hidden: { y: rm ? "0%" : "105%" },
    show: { y: "0%", transition: { duration: dur(0.95), ease: [0.2, 0.8, 0.2, 1] } },
  };
  const ruleDraw = {
    hidden: { scaleX: rm ? 1 : 0 },
    show: { scaleX: 1, transition: { duration: dur(1.1), ease: EASE } },
  };
  const swap = {
    hidden: { y: rm ? 0 : 14 },
    show: { y: 0, transition: { duration: dur(0.4), ease: EASE, delayChildren: dur(0.04), staggerChildren: dur(0.07) } },
    exit: { opacity: 0, y: rm ? 0 : -10, transition: { duration: dur(0.18) } },
  };

  // CTA hover/tap (hard offset shadow + arrow nudge)
  const ctaVariants = {
    hover: { x: -2, y: -2, boxShadow: `5px 5px 0px 0px ${INK}` },
    tap: { x: 0, y: 0, boxShadow: `0px 0px 0px 0px ${INK}` },
  };
  const ctaStyle = { boxShadow: `0px 0px 0px 0px ${INK}` };
  const arrowVariants = { hover: { x: rm ? 0 : 5 }, tap: { x: 0 } };

  const features = [
    "Multi-company ledger management",
    "GST-compliant invoicing & filings",
    "Real-time cash flow analytics",
    "Role-based access & audit trail",
  ];

  const tickerItems = [
    "Multi-company ledgers",
    "GST-compliant invoicing",
    "E-Invoice & E-Way Bill",
    "Real-time cash flow",
    "Role-based access",
    "Audit trail",
  ];

  const year = new Date().getFullYear();
  const stepNumber = isCompanySelectionVisible ? "02" : "01";

  const capsNote = (live) => (
    <div className="ld7-help" aria-live={live ? "polite" : undefined}>
      <AnimatePresence initial={false}>
        {capsLockOn && (
          <motion.span
            key="caps"
            className="ld7-caps"
            initial={{ opacity: 1, y: rm ? 0 : -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: dur(0.18) }}
          >
            <WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          className="ld7-alert-wrap"
          initial={{ opacity: 1, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: dur(0.25), ease: EASE }}
        >
          <motion.div
            key={errorMessage}
            className="ld7-alert-inner"
            animate={rm ? undefined : { x: [0, -6, 6, -4, 4, 0] }}
            transition={{ duration: 0.45, ease: "easeInOut" }}
          >
            <Alert variant="destructive" className="ld7-alert">
              <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0, mt: "1px" }} />
              <span>{errorMessage}</span>
            </Alert>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ThemeScope vars={THEME_VARS} className="ld7-root ld7-shell">
      <style>{LOGIN_CSS}</style>

      {/* ── FRAME ── */}
      <div className="ld7-frame">
        {/* ── MASTHEAD ── */}
        <motion.header
          className="ld7-masthead"
          initial={{ y: rm ? 0 : -14 }}
          animate={{ y: 0 }}
          transition={{ duration: dur(0.6), ease: EASE }}
        >
          <div className="ld7-mh-logo">
            <img src="/RBLOGOBLUE.svg" alt="ReonBook" />
          </div>
          <div className="ld7-mh-tag">
            <span className="ld7-mono">Financial intelligence for modern business</span>
          </div>
          <div className="ld7-mh-edition">
            <motion.span
              aria-hidden="true"
              className="ld7-dot"
              animate={rm ? undefined : { scale: [1, 1.5, 1], opacity: [1, 0.45, 1] }}
              transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }}
            />
            <Badge variant="outline" className="ld7-ed ld7-mono">Edition {year}</Badge>
          </div>
        </motion.header>

        {/* ── MAIN GRID ── */}
        <main className="ld7-main">
          {/* HERO */}
          <motion.section className="ld7-hero" variants={stagger(0.1, 0.14)} initial="hidden" animate="show">
            <motion.div className="ld7-eyebrow" variants={rise}>
              <span aria-hidden="true" className="ld7-eyebrow-sq" />
              <span className="ld7-eyebrow-txt ld7-mono">Enterprise Accounting Platform</span>
              <motion.span aria-hidden="true" className="ld7-eyebrow-rule" variants={ruleDraw} />
            </motion.div>

            <motion.h1 className="ld7-h1 ld7-display" variants={stagger(0, 0.12)}>
              <span className="ld7-line"><motion.span variants={lineRise}>All your</motion.span></span>
              <span className="ld7-line"><motion.span variants={lineRise}>books,</motion.span></span>
              <span className="ld7-line">
                <motion.span variants={lineRise}>
                  <span className="ld7-block">balanced<i>.</i></span>
                </motion.span>
              </span>
            </motion.h1>

            <motion.p className="ld7-lede" variants={rise}>
              Complete suite to manage invoices, GST compliance, cash flow, and multi-company ledgers — all in one place.
            </motion.p>
          </motion.section>

          {/* PANEL COLUMN */}
          <div className="ld7-panelcell">
            {/* Giant outlined step number (decorative) */}
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={stepNumber}
                aria-hidden="true"
                className="ld7-bignum ld7-display"
                initial={{ opacity: 1, y: rm ? 0 : 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: rm ? 0 : -40 }}
                transition={{ duration: dur(0.5), ease: EASE }}
              >
                {stepNumber}
              </motion.div>
            </AnimatePresence>

            <motion.div
              className="ld7-panelwrap"
              initial={{ y: rm ? 0 : 26 }}
              animate={{ y: 0 }}
              transition={{ duration: dur(0.8), ease: EASE, delay: dur(0.3) }}
            >
              <motion.div
                aria-hidden="true"
                className="ld7-shadowblock"
                initial={{ x: 0, y: 0 }}
                animate={{ x: 8, y: 8 }}
                transition={{ duration: dur(0.7), ease: EASE, delay: dur(0.75) }}
              />

              <Card className="ld7-panel">
                {/* Tab bar */}
                <div className="ld7-tab">
                  <span className="ld7-mono">{isCompanySelectionVisible ? "02 — Organization" : "01 — Sign in"}</span>
                  <i aria-hidden="true" />
                </div>

                <AnimatePresence mode="wait" initial={false}>
                  {!isCompanySelectionVisible ? (

                    /* ── LOGIN FORM ── */
                    <motion.div key="login" variants={swap} initial="hidden" animate="show" exit="exit">
                      <CardHeader className="ld7-ch">
                        <CardTitle className="ld7-ct ld7-display" role="heading" aria-level={2}>Welcome<i>.</i></CardTitle>
                        <CardDescription className="ld7-cd">Sign in to access your ReonBook dashboard</CardDescription>
                      </CardHeader>

                      <CardContent className="ld7-cc">
                        <form noValidate onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                          <motion.div className="ld7-field" variants={riseSafe}>
                            <Label htmlFor="ld7-identifier" className="ld7-lab ld7-mono">Username, Email or Mobile</Label>
                            <div className="ld7-inputwrap">
                              <Input id="ld7-identifier" type="text" value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                                autoComplete="username" autoCapitalize="none" spellCheck="false"
                              />
                              {capsLockOn && <WarningAmberIcon className="ld7-capsicon" />}
                            </div>
                            {capsNote(false)}
                          </motion.div>

                          <motion.div className="ld7-field ld7-pw" variants={riseSafe}>
                            <Label htmlFor="ld7-password" className="ld7-lab ld7-mono">Password</Label>
                            <div className="ld7-inputwrap">
                              <Input id="ld7-password" type={showPassword ? "text" : "password"} value={userPassword}
                                onChange={(e) => setUserPassword(e.target.value)}
                                onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                                autoComplete="current-password"
                              />
                              {capsLockOn && <WarningAmberIcon className="ld7-capsicon" />}
                              <MButton type="button" variant="ghost" size="icon" className="ld7-eye"
                                onClick={handleTogglePasswordVisibility}
                                aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}
                                whileTap={rm ? undefined : { scale: 0.88 }}>
                                {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                              </MButton>
                            </div>
                            {capsNote(true)}
                          </motion.div>

                          <motion.div className="ld7-forgot" variants={riseSafe}>
                            <Link to="/forgot-password" className="ld7-link ld7-tap" style={{ fontSize: "0.82rem" }}>
                              Forgot password?
                            </Link>
                          </motion.div>

                          {errorAlert}

                          <motion.div variants={riseSafe}>
                            <MButton type="submit" disabled={isLoading} className="ld7-cta"
                              variants={ctaVariants} style={ctaStyle}
                              whileHover={rm ? undefined : "hover"} whileTap={rm ? undefined : "tap"}>
                              {isLoading
                                ? <>
                                    <span>Signing in...</span>
                                    <CircularProgress size={18} thickness={5} sx={{ color: "inherit" }} />
                                  </>
                                : <>
                                    <span>Sign In</span>
                                    <motion.span className="ld7-cta-arrow" variants={arrowVariants}><ArrowForwardIcon sx={{ fontSize: 22 }} /></motion.span>
                                  </>}
                            </MButton>

                            {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                              <MButton type="button" variant="outline" className="ld7-bio"
                                whileTap={rm ? undefined : { scale: 0.98 }}
                                onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                                Login with Biometric
                              </MButton>
                            )}
                          </motion.div>
                        </form>
                      </CardContent>

                      <CardFooter className="ld7-cf">
                        <Separator />
                        <div className="ld7-signup">
                          <span>Don't have an account?</span>
                          <Link to="/register" className="ld7-link ld7-tap">Sign up now →</Link>
                        </div>
                      </CardFooter>
                    </motion.div>

                  ) : (

                    /* ── COMPANY SELECTION ── */
                    <motion.div key="company" variants={swap} initial="hidden" animate="show" exit="exit">
                      <CardHeader className="ld7-ch">
                        <CardTitle className="ld7-ct is-long ld7-display" role="heading" aria-level={2}>Select Organization<i>.</i></CardTitle>
                        <CardDescription className="ld7-cd">Choose which organization to access</CardDescription>
                      </CardHeader>

                      <CardContent className="ld7-cc">
                        <motion.div variants={riseSafe}>
                          <Label id="ld7-company-label" className="ld7-lab ld7-mono">Choose your organization</Label>
                          <div role="radiogroup" aria-labelledby="ld7-company-label" className="ld7-colist">
                            {companies.map((c, i) => {
                              const checked = selectedCompany?.companyId === c.companyId;
                              return (
                                <MCard
                                  key={c.companyId}
                                  className={"ld7-co" + (checked ? " is-selected" : "")}
                                  initial={{ y: rm ? 0 : 12 }}
                                  animate={{ y: 0 }}
                                  transition={{ duration: dur(0.4), ease: EASE, delay: dur(0.12 + i * 0.06) }}
                                  whileHover={rm ? undefined : { x: -2, y: -2 }}
                                  whileTap={rm ? undefined : { x: 0, y: 0, scale: 0.99 }}
                                >
                                  <label className="ld7-co-label">
                                    <input
                                      type="radio"
                                      name="ld7-company"
                                      className="ld7-sr"
                                      value={String(c.companyId)}
                                      checked={checked}
                                      onChange={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                                    />
                                    <span className="ld7-co-num ld7-mono">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="ld7-co-name">{c.companyName}</span>
                                    <span className="ld7-co-check" aria-hidden="true">
                                      {checked && <CheckIcon sx={{ fontSize: 16 }} />}
                                    </span>
                                  </label>
                                </MCard>
                              );
                            })}
                          </div>
                        </motion.div>

                        {errorAlert}

                        <motion.div variants={riseSafe}>
                          <MButton type="button" className="ld7-cta" onClick={() => FN_SelectCompany()}
                            variants={ctaVariants} style={ctaStyle}
                            whileHover={rm ? undefined : "hover"} whileTap={rm ? undefined : "tap"}>
                            <span>Continue to Dashboard</span>
                            <motion.span className="ld7-cta-arrow" variants={arrowVariants}><ArrowForwardIcon sx={{ fontSize: 22 }} /></motion.span>
                          </MButton>
                        </motion.div>
                      </CardContent>

                      <CardFooter className="ld7-cf" style={{ paddingTop: 0 }} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          </div>

          {/* FEATURES (ruled grid) */}
          <motion.ul className="ld7-features" variants={stagger(0.55, 0.09)} initial="hidden" animate="show">
            {features.map((feat, i) => (
              <motion.li
                key={feat}
                variants={rise}
                style={{ backgroundColor: SAND }}
                whileHover={rm ? undefined : { backgroundColor: PAPER }}
              >
                <Badge className="ld7-fbadge ld7-mono">{String(i + 1).padStart(2, "0")}</Badge>
                <span>{feat}</span>
              </motion.li>
            ))}
          </motion.ul>
        </main>

        {/* ── TICKER ── */}
        <div className="ld7-ticker" aria-hidden="true">
          <div className="ld7-track ld-motion">
            {[0, 1, 2, 3].map((g) => (
              <div key={g} className="ld7-tgroup">
                {tickerItems.map((t) => (
                  <span key={t} className="ld7-titem">
                    <span className="t ld7-display">{t}</span>
                    <span className="ld7-diamond" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer className="ld7-footer ld7-mono">
          <p>
            <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.5, mt: "-2px" }} />
            {year} Powered by{" "}
            {/* <a href="https://soltrack.co.in/" target="_blank" rel="noopener noreferrer" style={{ color: "#475569", fontWeight: 600, textDecoration: "none" }}> */}
            Soltrack Technologies Pvt Ltd
            {/* </a> */}
          </p>
          <p className="ld7-footer-r">ReonBook — Accounting Platform</p>
        </footer>
      </div>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="ld7-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        aria-labelledby="ld7-session-title"
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={rm ? 0 : { enter: 320, exit: 180 }}
        BackdropProps={{ sx: { backdropFilter: "blur(3px)", bgcolor: "rgba(23,18,12,0.55)" } }}
        PaperProps={{ sx: { borderRadius: 0, overflow: "hidden", bgcolor: PAPER, border: `2px solid ${INK}`, boxShadow: `8px 8px 0 ${ACCENT}`, m: { xs: 2, sm: 4 }, width: { xs: "calc(100% - 40px)", sm: "calc(100% - 64px)" }, maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" } } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="ld7-dlg">
            {/* Top banner */}
            <div className="ld7-dlg-banner">
              <div className="ld7-dlg-head">
                <div className="ld7-dlg-icon"><DevicesIcon sx={{ fontSize: 24 }} /></div>
                <div style={{ minWidth: 0 }}>
                  <h2 id="ld7-session-title" className="ld7-dlg-title ld7-display">Session Limit Reached</h2>
                  <p className="ld7-dlg-sub ld7-mono">
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </p>
                </div>
              </div>
              <p className="ld7-dlg-note">Sign out of one of the devices below to log in on this device.</p>
            </div>

            {/* Device list */}
            <div className="ld7-dlg-body">
              <div className="ld7-dlg-label">
                <span className="ld7-mono">Active Sessions</span>
                <Separator />
              </div>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <motion.div
                  key={session.id}
                  className="ld7-sess"
                  initial={{ y: rm ? 0 : 12 }}
                  animate={{ y: 0 }}
                  transition={{ duration: dur(0.45), ease: EASE, delay: dur(0.1 + idx * 0.07) }}
                  whileHover={rm ? undefined : { x: -2, y: -2 }}
                >
                  {/* Icon */}
                  <div className="ld7-sess-icon">{deviceIcon(session.device_type)}</div>

                  {/* Info */}
                  <div className="ld7-sess-info">
                    <div className="ld7-sess-name">{session.device_name || "Unknown Device"}</div>
                    <div className="ld7-sess-meta">
                      <AccessTimeIcon sx={{ fontSize: 12, color: MUTED, flexShrink: 0 }} />
                      <span className="ld7-mono">
                        {timeAgo(session.last_activity)}
                        {session.ip_address && ` · ${session.ip_address}`}
                      </span>
                    </div>
                  </div>

                  {/* Action */}
                  <MButton
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="ld7-signout"
                    disabled={kickingSessionId === session.id}
                    whileTap={rm ? undefined : { scale: 0.95 }}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </MButton>
                </motion.div>
              ))}
            </div>

            {/* Cancel */}
            <div className="ld7-dlg-foot">
              <MButton type="button" variant="outline" className="ld7-cancel"
                whileTap={rm ? undefined : { scale: 0.98 }}
                onClick={() => setSessionPickerData(null)}>
                Cancel
              </MButton>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign07;
